'use client';
import cx from 'classnames';
import { Key, MouseEvent, ReactNode } from 'react';

import { Dialog, dialog } from '@flipster/react/design-system/dialog';

import { Button } from '../Button';
import CircleInfoFillIcon from '../assets/svgs/circle-info-fill.svg';
import SortUpIcon from '../assets/svgs/sort-up.svg';
import styles from './tableHeader.module.scss';
import { AlignType } from './types';

type OrderByType = 'asc' | 'desc' | undefined;

export interface Col {
  key: Key;
  title: ReactNode;
  align?: AlignType;
  sortKey?: string;
  subColumns?: { key?: Key; title: ReactNode; sortKey?: string }[];
  tooltip?: {
    icon?: ReactNode;
    title?: string;
    message: string;
  };
  className?: string;
  width?: number | string;
  minWidth?: number;
}

interface Props {
  columns: Col[];
  bold?: boolean;
  sortKey?: string;
  sortOrder?: OrderByType;
  onChangeSorting?: (sorting: [key: string | undefined, orderBy: OrderByType]) => unknown;
  className?: string;
}

export const TableHeader = ({ columns, bold, sortKey, sortOrder, onChangeSorting, className }: Props) => {
  const onClickSorting = (e: MouseEvent<HTMLButtonElement>) => {
    const key = e.currentTarget.value;
    if (key === sortKey && sortOrder === 'asc') {
      onChangeSorting?.([undefined, undefined]);
      return;
    }
    onChangeSorting?.([key, key !== sortKey ? 'desc' : 'asc']);
  };

  const renderHeaderItem = (itemKey: Key, title: ReactNode, itemSortKey?: string) => {
    if (!itemSortKey) {
      return (
        <span key={itemKey} className={styles.title}>
          {title}
        </span>
      );
    }
    return (
      <button type='button' key={itemKey} value={itemSortKey} onClick={onClickSorting} className={styles.sorting}>
        <span className={cx(styles.title, { [styles.sorted]: !!sortOrder && sortKey === itemSortKey })}>{title}</span>
        <div className={cx(styles.icon, { [styles[sortOrder!]]: sortOrder && sortKey === itemSortKey })}>
          <span className={styles.up}>
            <SortUpIcon />
          </span>
          <span className={styles.down}>
            <SortUpIcon />
          </span>
        </div>
      </button>
    );
  };

  const isFixedWidth = columns.some((col) => col.width);

  return (
    <>
      {isFixedWidth && (
        <colgroup>
          {columns.map((col) => (
            <col key={`colgroup-${col.key}`} width={col.width} />
          ))}
        </colgroup>
      )}
      <thead className={cx(styles.wrapper, className, { [styles.bold]: bold })}>
        <tr>
          {columns.map((col) => {
            return (
              <th
                className={col.className}
                key={`th-${col.key}`}
                // safari에서는 colgroup의 minWidth가 작동하지 않아, 각 th마다 minWidth를 지정해줘야 함
                style={{ minWidth: col.minWidth, width: col.width }}
              >
                <div className={styles[col.align ?? 'left']}>
                  {col.subColumns ? (
                    <div className={styles.subColumns}>
                      {col.subColumns.map((sub) =>
                        renderHeaderItem(sub.key ?? `${col.key}-${sub.sortKey}`, sub.title, sub.sortKey),
                      )}
                    </div>
                  ) : (
                    renderHeaderItem(col.key, col.title, col.sortKey)
                  )}
                  {col.tooltip && (
                    <Button
                      shape='trans'
                      className={styles.tooltip}
                      onClick={() => {
                        dialog.open({
                          title: {
                            label: col.tooltip?.title ?? 'Notice',
                          },
                          content: col.tooltip?.message,
                          buttons: {
                            primary: <Dialog.Button hierarchy='primary' label='OK' />,
                          },
                        });
                      }}
                    >
                      {col.tooltip.icon || <CircleInfoFillIcon role='tooltip' />}
                    </Button>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    </>
  );
};
